import { useCanvasStore } from '../store/canvas-store';
import { getRenderHtml, framesById } from '../store/commands';
import { shortId } from '../chrome/dropDetect';
import type { EaseSpec, MotionProp, MotionTimeline, PlaybackConfig } from '../store/types';

interface BatchChannel {
  prop: MotionProp;
  keyframes: Array<{ t: number; value: number | string; ease?: EaseSpec }>;
}

export interface MotionApplyInput {
  frameId: string;
  duration?: number;
  playback?: PlaybackConfig;
  tracks: Array<{
    target: string;
    label?: string;
    layers?: Array<{ blend?: 'replace' | 'add'; channels: BatchChannel[] }>;
    /** Shorthand: channels directly on the track = one replace-blend layer. */
    channels?: BatchChannel[];
  }>;
}

export interface MotionApplyResult { ok: boolean; error?: string; warnings: string[] }

/** Frame kinds that can carry a motion timeline. */
function isAnimatable(kind: string): boolean {
  return kind === 'free' || kind === 'main' || kind === 'tsxFree' || kind === 'tsxMain';
}

/**
 * Expand one batch description into the existing motion.* command vocabulary,
 * inside one transaction (one undo step, one CRDT transaction). Tracks upsert
 * by target: a named target's existing track is removed first; other tracks are
 * untouched. Unknown cf-ids are skipped with a warning so the agent can correct
 * itself without a failed turn.
 *
 * Transaction nesting is a depth counter (canvas-store `txnDepth`): nested
 * begin/end is safe — only the outermost begin/end commits, so this works the
 * same whether or not an agent turn already opened a transaction.
 */
export function applyMotionBatch(input: MotionApplyInput): MotionApplyResult {
  const store = useCanvasStore.getState();
  const frame = store.doc.frames.find((f) => f.id === input.frameId);
  if (!frame || !isAnimatable(frame.kind)) {
    return { ok: false, error: `frame ${input.frameId} not found or not animatable`, warnings: [] };
  }
  const byId = framesById(store.doc.frames);
  // cf-ids available for this frame come from two sources: the rendered html
  // (free/main — works in tests + headless) and the live, cf-id-stamped DOM
  // (tsx frames produce no html via getRenderHtml; their cf-ids exist only on
  // the mounted React tree, stamped by stampDomCfIds at render).
  const htmlHost = document.createElement('div');
  htmlHost.innerHTML = getRenderHtml(frame, byId);
  const liveRoot = typeof document !== 'undefined'
    ? document.querySelector(`[data-frame-id="${input.frameId}"]`)
    : null;
  const hasTarget = (cfId: string): boolean =>
    !!htmlHost.querySelector(`[data-cf-id="${cfId}"]`) ||
    !!liveRoot?.querySelector(`[data-cf-id="${cfId}"]`);
  const warnings: string[] = [];

  // dispatch() rebuilds doc on each call, so the captured getState() snapshot
  // goes stale — always re-read getState() to see the post-dispatch timeline.
  const tlOf = (): MotionTimeline | undefined =>
    (useCanvasStore.getState().doc.frames.find((f) => f.id === input.frameId) as { motion?: MotionTimeline }).motion;
  const dispatch = useCanvasStore.getState().dispatch;

  useCanvasStore.getState().beginTransaction('apply motion');
  try {
    if (!tlOf()) dispatch({ t: 'motion.init', frameId: input.frameId });

    for (const tr of input.tracks) {
      if (!hasTarget(tr.target)) {
        warnings.push(`target ${tr.target} not found in frame ${input.frameId} — track skipped`);
        continue;
      }
      const existing = tlOf()!.tracks.find((x) => x.target === tr.target);
      if (existing) dispatch({ t: 'motion.removeTrack', frameId: input.frameId, trackId: existing.id });

      const layers = tr.layers ?? [{ blend: 'replace' as const, channels: tr.channels ?? [] }];
      const trackId = shortId();
      const firstLayerId = shortId();
      dispatch({ t: 'motion.addTrack', frameId: input.frameId, trackId, layerId: firstLayerId, target: tr.target, label: tr.label });
      layers.forEach((ly, i) => {
        let layerId = firstLayerId;
        if (i > 0) {
          layerId = shortId();
          dispatch({ t: 'motion.addLayer', frameId: input.frameId, trackId, layerId, blend: ly.blend ?? 'add' });
        } else if (ly.blend && ly.blend !== 'replace') {
          // addTrack always seeds a 'replace' first layer; honor an explicit blend.
          dispatch({ t: 'motion.setLayerBlend', frameId: input.frameId, trackId, layerId, blend: ly.blend });
        }
        for (const ch of ly.channels) {
          for (const kf of ch.keyframes) {
            dispatch({ t: 'motion.setKeyframe', frameId: input.frameId, trackId, layerId, prop: ch.prop, kf: { t: kf.t, value: kf.value, ease: kf.ease ?? 'easeInOut' } });
          }
        }
      });
    }
    if (input.duration !== undefined) dispatch({ t: 'motion.setDuration', frameId: input.frameId, duration: input.duration });
    if (input.playback) dispatch({ t: 'motion.setPlayback', frameId: input.frameId, playback: input.playback });
  } finally {
    useCanvasStore.getState().endTransaction();
  }
  return { ok: true, warnings };
}
